import { FallbackPriceTierOut, ProductOut } from '../../store/api';
import { globalErrorResponse, showNotification, TYPE_WARNING } from '../../store/notifications/actions';
import { invalidateCartSummaryQuery } from './useCartSummaryQuery';
import { failUpdateToCart } from '../../store/products/actionsCart';
import { AppDispatch } from '../../store';
import { ListContentSource } from '../../store/constants';
import { TFunction } from 'i18next';
import { ProductSelection } from '../../shared/product-selection/ProductSelectionTypes';

type Params<T> = {
    dispatch: AppDispatch;
    setLoading: (loading: boolean) => void;
    performChecks: boolean;
    source: ListContentSource;
    notificationMessage?: string | ((responseData: T) => string);
    apiCallback: () => Promise<T>;
    t: TFunction;
    onSuccess: () => void;
    productSelection: ProductSelection;
};

interface ResponseType {
    readonly fallbackPriceTiers?: ReadonlyArray<FallbackPriceTierOut>;
    readonly unavailableProducts?: Array<ProductOut>;
    readonly preOrderedProducts?: Array<ProductOut>;
    readonly alreadyInListProducts?: Array<ProductOut>;
    readonly hasEpress?: boolean;
}

export function bulkUpdateToCart<T extends ResponseType>({
    dispatch,
    setLoading,
    performChecks,
    source,
    notificationMessage,
    apiCallback,
    t,
    onSuccess,
    productSelection
}: Params<T>): Promise<T> {
    setLoading(true);

    return apiCallback()
        .then(data => {
            const fallbackPriceTiers = data.fallbackPriceTiers ?? [];
            const alreadyInListProducts = data.alreadyInListProducts ?? [];
            const preOrderedProducts = data.preOrderedProducts ?? [];

            if (
                fallbackPriceTiers.length === 0 &&
                alreadyInListProducts.length === 0 &&
                preOrderedProducts.length === 0
            ) {
                if (data.hasEpress) {
                    dispatch(showNotification(t('notifications.hasEpress', { context: 'cart' }), TYPE_WARNING));
                }

                const message =
                    typeof notificationMessage === 'function' ? notificationMessage(data) : notificationMessage;

                dispatch(showNotification(message ?? t('notifications.addToCartBulk.success')));
                invalidateCartSummaryQuery().then();

                onSuccess();
            }
            return data;
        })
        .catch(error => {
            if (!performChecks) {
                dispatch(failUpdateToCart({ t, error, source, productSelection, onSuccess }));
            } else {
                dispatch(globalErrorResponse(error));
            }
            return Promise.reject(error);
        })
        .finally(() => setLoading(false));
}
